import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link as MuiLink,
} from '@mui/material';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { findingsAPI } from '../../services/api';
import { rankLabel } from './sampleFindings';
import { formatProjectLabel } from '../../utils/formatProjectLabel';

/**
 * Cohort comparison — the "Compare" tab (#57).
 *
 * Within a cohort (a study by default, optionally narrowed to one project, or a
 * standalone project), pick a result type that has data, group by a clinical
 * metadata variable, and compare across the groups. Kind-aware: the comparison
 * shape and chart come from the backend's `comparison` descriptor, not hardcoded.
 *
 *   - taxon          → per-group PREVALENCE (presence/absence). Two real groups
 *                      get a diverging difference chart (where do they diverge,
 *                      in whose favor); three or more get grouped bars.
 *   - classification → per-group DISTRIBUTION of one label per specimen, drawn as
 *                      a 100%-stacked bar per group.
 *
 * Grouping variables and result types are discovered at runtime per cohort — no
 * fixed field list, no fixed kind list. The same entity named differently by
 * different pipelines is counted separately (exact-name matching); the backend
 * surfaces that note. This is a hypothesis-generating view, not a statistical test.
 */

const RANKS = ['domain', 'phylum', 'class', 'order', 'family', 'genus', 'species'];

// Okabe-Ito palette — chosen for maximum mutual distinguishability AND
// colorblind safety (matters for plots scientists share). Groups and classes are
// dynamic, so we cycle. Black replaced with grey so segments read on white.
const SERIES_COLORS = [
  '#0072B2', // blue
  '#E69F00', // orange
  '#009E73', // bluish green
  '#CC79A7', // reddish purple
  '#56B4E9', // sky blue
  '#D55E00', // vermillion
  '#F0E442', // yellow
  '#999999', // grey
];
// Diverging chart: blue vs vermillion — a classic two-sided, colorblind-safe pair.
const FAVOR_POS = '#0072B2'; // favors the first group (right)
const FAVOR_NEG = '#D55E00'; // favors the second group (left)
const CHART_ROWS = 8; // top categories (by gap) for the 2-group diverging chart
const HEATMAP_ROWS = 15; // top taxa (by gap) for the multi-group heatmap

// Sequential white→blue ramp for the prevalence heatmap. t in [0,1].
const heatColor = (t) => {
  const c = Math.max(0, Math.min(1, t));
  const r = Math.round(255 + (0 - 255) * c);
  const g = Math.round(255 + (114 - 255) * c);
  const b = Math.round(255 + (178 - 255) * c);
  return `rgb(${r}, ${g}, ${b})`;
};

// Pick the default "Group by" field: the most readable comparison out of the
// gate — fewest groups, well-populated — rather than whatever happens to sort
// first (which can be a high-cardinality ordinal like a 20-value clinical score
// that opens on a wall of tiny groups). Among well-filled fields, prefer the
// lowest cardinality; tie-break by completeness, then boolean/categorical over
// many-valued integer, then name. Returns '' for an empty list.
function pickDefaultGroupField(fields) {
  if (!fields || fields.length === 0) return '';
  const typeRank = (t) => (t === 'boolean' ? 0 : t === 'categorical' ? 1 : 2);
  const byUsability = (a, b) =>
    a.unique_count - b.unique_count ||
    b.completeness - a.completeness ||
    typeRank(a.type) - typeRank(b.type) ||
    a.name.localeCompare(b.name);
  const wellFilled = fields.filter((f) => f.completeness >= 30);
  const pool = wellFilled.length ? wellFilled : fields;
  return [...pool].sort(byUsability)[0].name;
}

const CohortComparison = ({ scopeType, scopeId, projects = [] }) => {
  const isStudy = scopeType === 'study';

  // Result types = one entry per (pipeline, kind) with data. The user picks a
  // concrete analysis ("CST", "16S taxonomy"); each entry pins its own pipeline.
  const [resultTypes, setResultTypes] = useState([]);
  const [rtLoading, setRtLoading] = useState(true);
  const [rtError, setRtError] = useState(null);
  const [resultTypeId, setResultTypeId] = useState('');

  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldsError, setFieldsError] = useState(null);

  const [field, setField] = useState('');
  const [rank, setRank] = useState('genus');
  const [narrowProject, setNarrowProject] = useState(''); // '' = whole study
  const [minGroupSize, setMinGroupSize] = useState(10); // groups smaller than this are shown but not compared
  // Binning (continuous group-by) + metric (prevalence vs relative abundance).
  const [binMethod, setBinMethod] = useState(''); // '' unless the field needs binning
  const [binThresholds, setBinThresholds] = useState(''); // comma-separated, for custom
  const [metric, setMetric] = useState('prevalence'); // taxon only; 'prevalence' | 'abundance'

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const narrowParam = narrowProject ? { project_id: narrowProject } : undefined;

  // Discover result types with data in this scope (re-runs when narrowed, since a
  // project may carry different analyses than its study).
  useEffect(() => {
    if (!scopeId) return undefined;
    let active = true;
    setRtLoading(true);
    setRtError(null);
    const call = isStudy
      ? findingsAPI.studyResultTypes(scopeId, narrowParam)
      : findingsAPI.projectResultTypes(scopeId);
    call
      .then((res) => {
        if (!active) return;
        const rt = res.data.result_types || [];
        setResultTypes(rt);
        setResultTypeId((prev) => (rt.some((x) => x.id === prev) ? prev : rt[0]?.id || ''));
      })
      .catch((err) => {
        if (active) setRtError(err.response?.data?.msg || err.message || 'Failed to load result types');
      })
      .finally(() => active && setRtLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, isStudy, narrowProject]);

  // Discover groupable fields for the current scope.
  useEffect(() => {
    if (!scopeId) return undefined;
    let active = true;
    setFieldsLoading(true);
    setFieldsError(null);
    const call = isStudy
      ? findingsAPI.studyGroupableFields(scopeId, narrowParam)
      : findingsAPI.projectGroupableFields(scopeId);
    call
      .then((res) => {
        if (!active) return;
        const f = res.data.fields || [];
        setFields(f);
        setField((prev) => (f.some((x) => x.name === prev) ? prev : pickDefaultGroupField(f)));
      })
      .catch((err) => {
        if (active) setFieldsError(err.response?.data?.msg || err.message || 'Failed to load fields');
      })
      .finally(() => active && setFieldsLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, isStudy, narrowProject]);

  const selectedEntry = useMemo(
    () => resultTypes.find((r) => r.id === resultTypeId) || null,
    [resultTypes, resultTypeId]
  );

  // The chosen grouping field's descriptor. Continuous fields must be binned;
  // discrete fields group per value.
  const selectedField = useMemo(() => fields.find((f) => f.name === field) || null, [fields, field]);
  const needsBinning = !!selectedField?.requires_binning;
  const parsedThresholds = useMemo(
    () => binThresholds.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n)),
    [binThresholds]
  );
  // Custom binning can't run until at least one threshold is entered.
  const binReady = !needsBinning || binMethod !== 'custom' || parsedThresholds.length >= 1;
  // Data-driven from the result type's descriptor: a kind offers the relative-
  // abundance lens when it declares a numeric weight (taxon does; host_prediction
  // / classification don't). No kind names hardcoded here.
  const canAbundance = !!selectedEntry?.has_abundance;

  // When the grouping field changes, default the bin method: continuous fields
  // start on tertiles; discrete fields turn binning off.
  useEffect(() => {
    setBinMethod(needsBinning ? 'tertiles' : '');
  }, [field, needsBinning]);

  // Abundance is taxon-only; revert to prevalence when the result type can't do it.
  useEffect(() => {
    if (!canAbundance) setMetric('prevalence');
  }, [canAbundance]);

  // Fetch the comparison whenever the result type / field / rank / narrow changes.
  // The entry pins kind + pipeline, so a comparison is always one coherent analysis.
  useEffect(() => {
    if (!scopeId || !field || !selectedEntry) {
      setData(null);
      return undefined;
    }
    // Continuous field on custom binning with no thresholds yet: hold off — the
    // controls show a hint instead of firing a request that would 400.
    if (!binReady) {
      setData(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const params = {
      kind: selectedEntry.kind,
      producer: selectedEntry.producer_name,
      field,
      min_group_size: minGroupSize,
      ...(selectedEntry.has_rank ? { rank } : {}),
      ...(needsBinning && binMethod
        ? {
            bin_method: binMethod,
            ...(binMethod === 'custom' ? { bin_thresholds: parsedThresholds.join(',') } : {}),
          }
        : {}),
      ...(canAbundance && metric === 'abundance' ? { metric: 'abundance' } : {}),
      ...(isStudy && narrowProject ? { project_id: narrowProject } : {}),
    };
    const call = isStudy
      ? findingsAPI.studyCohortComparison(scopeId, params)
      : findingsAPI.projectCohortComparison(scopeId, params);
    call
      .then((res) => active && setData(res.data))
      .catch((err) => {
        if (active) setError(err.response?.data?.msg || err.message || 'Failed to build comparison');
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, isStudy, resultTypeId, field, rank, narrowProject, minGroupSize, selectedEntry?.has_rank,
      needsBinning, binMethod, parsedThresholds.join(','), binReady, canAbundance, metric]);

  const comparison = data?.comparison || null;
  const groups = useMemo(() => data?.groups || [], [data]);
  const rows = useMemo(() => data?.rows || [], [data]);
  // Comparable groups (real, big enough) drive the charts; below-threshold and
  // Unknown groups are shown elsewhere but never plotted as a comparison.
  const comparableGroups = useMemo(() => groups.filter((g) => g.comparable), [groups]);
  // When comparable groups differ a lot in size, small-group estimates are noisy
  // (and can read 0% from under-sampling) — flag it so the plot isn't over-read.
  const unevenNote = useMemo(() => {
    const dens = comparableGroups.map((g) => g.denominator).filter((d) => d > 0);
    if (dens.length < 2) return null;
    const min = Math.min(...dens);
    const max = Math.max(...dens);
    if (max / min < 5) return null;
    return { min, max };
  }, [comparableGroups]);
  const categoryLabel = comparison?.category_label || 'Category';
  const categoryPlural = comparison?.category_label_plural || `${categoryLabel}s`;
  // Rank-based (taxonomy-shaped) kinds — taxon, host_prediction — have ranked
  // categories that link to taxon search and read "at the <rank> rank". A kind
  // without a rank (classification) renders plain category text. Driven by the
  // descriptor, not the kind name.
  const isRankBased = !!comparison?.has_rank;
  // Both kinds report a per-group denominator distinct from the group total, so
  // coverage (denominator / total) is shown: for taxon it's specimens the run
  // ASSAYED (prevalence is over assayed, not all study specimens); for
  // classification it's specimens that carry a class label.
  const showsCoverage = groups.some((g) => g.denominator !== g.total);
  const coverageNoun = comparison?.mode === 'distribution' ? 'classified' : 'assayed';

  const taxonLink = (name) =>
    `/analysis/taxon-search?name=${encodeURIComponent(name)}&rank=${encodeURIComponent(rank)}`;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Group this {isStudy ? 'study' : 'project'} by a clinical variable and compare results across
        the groups. {data?.metric === 'abundance'
          ? 'Relative abundance is the median within-specimen share of each taxon across a group’s specimens (absent counts as 0%).'
          : comparison?.mode === 'distribution'
            ? 'Distribution is the share of a group’s classified specimens in each class.'
            : 'Prevalence is the share of a group’s specimens a result appears in.'}{' '}
        This is a hypothesis-generating view, not a statistical test.
      </Typography>

      {rtError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load result types: {rtError}</Alert>}
      {fieldsError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load grouping fields: {fieldsError}</Alert>}

      {!rtLoading && !rtError && resultTypes.length === 0 && (
        <Alert severity="info">
          No analysis results have been uploaded for this {isStudy ? 'study' : 'project'} yet, so there
          is nothing to compare.
        </Alert>
      )}

      {!fieldsLoading && !fieldsError && resultTypes.length > 0 && fields.length === 0 && (
        <Alert severity="info">
          No groupable metadata fields found for this {isStudy ? 'study' : 'project'}. Upload specimen
          metadata with at least one categorical variable (e.g. case/control) to compare cohorts.
        </Alert>
      )}

      {(rtLoading || resultTypes.length > 0) && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Result type"
            value={resultTypeId}
            onChange={(e) => setResultTypeId(e.target.value)}
            disabled={rtLoading || resultTypes.length === 0}
            sx={{ minWidth: 200 }}
          >
            {resultTypes.map((rt) => (
              <MenuItem key={rt.id} value={rt.id}>
                {rt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Group by"
            value={field}
            onChange={(e) => setField(e.target.value)}
            disabled={fieldsLoading || fields.length === 0}
            sx={{ minWidth: 220 }}
          >
            {fields.map((f) => (
              <MenuItem key={f.name} value={f.name}>
                {f.name} ({f.unique_count} values · {Math.round(f.completeness)}% filled)
              </MenuItem>
            ))}
          </TextField>

          {selectedEntry?.has_rank && (
            <TextField
              select
              size="small"
              label="Rank"
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              {RANKS.map((r) => (
                <MenuItem key={r} value={r}>
                  {rankLabel(r)}
                </MenuItem>
              ))}
            </TextField>
          )}

          {needsBinning && (
            <TextField
              select
              size="small"
              label="Bins"
              value={binMethod}
              onChange={(e) => setBinMethod(e.target.value)}
              helperText="Continuous — grouped into bins"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="median">Median split</MenuItem>
              <MenuItem value="tertiles">Tertiles (low/mid/high)</MenuItem>
              <MenuItem value="quartiles">Quartiles</MenuItem>
              <MenuItem value="custom">Custom thresholds…</MenuItem>
            </TextField>
          )}

          {needsBinning && binMethod === 'custom' && (
            <TextField
              size="small"
              label="Thresholds"
              placeholder="e.g. 50, 200"
              value={binThresholds}
              onChange={(e) => setBinThresholds(e.target.value)}
              error={!binReady}
              helperText={binReady ? 'Comma-separated cutoffs' : 'Enter at least one number'}
              sx={{ width: 170 }}
            />
          )}

          {canAbundance && (
            <TextField
              select
              size="small"
              label="Metric"
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              helperText={metric === 'abundance' ? 'Median rel. abundance' : 'Presence/absence'}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="prevalence">Prevalence</MenuItem>
              <MenuItem value="abundance">Relative abundance</MenuItem>
            </TextField>
          )}

          <TextField
            type="number"
            size="small"
            label="Min. group size"
            value={minGroupSize}
            onChange={(e) => {
              const n = Math.max(0, Math.floor(Number(e.target.value)) || 0);
              setMinGroupSize(n);
            }}
            inputProps={{ min: 0, step: 5 }}
            helperText="Smaller groups shown, not compared"
            sx={{ width: 150 }}
          />

          {isStudy && projects.length > 1 && (
            <TextField
              select
              size="small"
              label="Narrow to project"
              value={narrowProject}
              onChange={(e) => setNarrowProject(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Whole study</MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {formatProjectLabel(p)}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      )}

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Building comparison…</Typography>
        </Stack>
      )}

      {error && <Alert severity="error">Failed to build comparison: {error}</Alert>}

      {!loading && !error && data && (
        <>
          {data.note && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {data.note}
            </Alert>
          )}

          {data.binning && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {data.field?.name} binned by {data.binning.method}
              {data.binning.cutpoints?.length
                ? ` · cutpoints ${data.binning.cutpoints.join(', ')}`
                : ''}
            </Typography>
          )}

          {/* Group sizes. Coverage (denominator/total) shown consistently for every
              group when the denominator differs from the total — "assayed" for
              taxon prevalence, "classified" for classification. Below-threshold
              groups are shown but flagged — never hidden. */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {groups.map((g) => {
              const sizeText = showsCoverage
                ? `${g.denominator.toLocaleString()}/${g.total.toLocaleString()} ${coverageNoun}`
                : g.total.toLocaleString();
              const suffix = g.below_threshold ? ' · too few to compare' : '';
              const muted = g.key === 'Unknown' || g.below_threshold;
              return (
                <Chip
                  key={g.key}
                  label={`${g.label}: ${sizeText}${suffix}`}
                  variant={muted ? 'outlined' : 'filled'}
                  color={muted ? 'default' : 'primary'}
                  size="small"
                />
              );
            })}
          </Stack>

          {unevenNote && comparableGroups.length >= 2 && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
              Groups differ a lot in size (n={unevenNote.min.toLocaleString()} to{' '}
              n={unevenNote.max.toLocaleString()}). Estimates from the smaller groups are noisier and
              can read 0% just from under-sampling — weight them accordingly.
            </Alert>
          )}

          <ComparisonChart
            comparison={comparison}
            comparableGroups={comparableGroups}
            rows={rows}
            metric={data.metric}
          />

          {rows.length === 0 ? (
            <Alert severity="info">
              No {categoryLabel.toLowerCase()} findings for this cohort
              {isRankBased ? ` at the ${rankLabel(rank)} rank` : ''} yet.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{categoryLabel}</TableCell>
                    {groups.map((g) => (
                      <TableCell
                        key={g.key}
                        align="right"
                        sx={{ color: g.comparable ? 'text.primary' : 'text.disabled' }}
                      >
                        {g.label}
                        <Typography variant="caption" color="text.secondary" display="block">
                          n={g.denominator}
                        </Typography>
                        {g.below_threshold && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            too few
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="right">Gap</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.category} hover>
                      <TableCell>
                        {isRankBased ? (
                          <MuiLink
                            component={RouterLink}
                            to={taxonLink(row.category)}
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {row.category}
                          </MuiLink>
                        ) : (
                          row.category
                        )}
                      </TableCell>
                      {groups.map((g) => {
                        const cell = row.cells[g.key] || { pct: 0 };
                        return (
                          <TableCell key={g.key} align="right">
                            {cell.pct}%
                            {cell.count != null && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {cell.count}/{g.denominator}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <strong>{row.gap}%</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {data.truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing the top {rows.length} of {data.total_categories} {categoryPlural.toLowerCase()} by
              between-group gap.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

// Dispatch to the viewer for this comparison's mode. Prevalence (taxon) adapts to
// the group count; distribution (classification) is a 100%-stacked bar per group.
// Only comparable groups (real + above the size threshold) are plotted.
const ComparisonChart = ({ comparison, comparableGroups, rows, metric }) => {
  if (!comparison || rows.length === 0) return null;
  if (comparableGroups.length < 2) return null; // nothing to compare; note covers it

  if (comparison.mode === 'distribution') {
    return <DistributionChart comparableGroups={comparableGroups} rows={rows} />;
  }
  return <PrevalenceChart comparison={comparison} comparableGroups={comparableGroups} rows={rows} metric={metric} />;
};

// Prevalence/abundance share the same diverging-bars (2 groups) / heatmap (3+)
// layout — the cell value is a percentage either way; only the wording differs.
const PrevalenceChart = ({ comparison, comparableGroups, rows, metric }) => {
  const categoryPlural = comparison.category_label_plural || `${comparison.category_label}s`;
  const valueNoun = metric === 'abundance' ? 'median relative abundance' : 'prevalence';

  if (comparableGroups.length !== 2) {
    return (
      <PrevalenceHeatmap
        comparableGroups={comparableGroups}
        rows={rows}
        categoryPlural={categoryPlural}
        valueNoun={valueNoun}
      />
    );
  }

  const top = rows.slice(0, CHART_ROWS);
  {
    const [gA, gB] = comparableGroups;
    const diffData = top.map((r) => ({
      category: r.category,
      diff: Math.round((r.cells[gA.key].pct - r.cells[gB.key].pct) * 10) / 10,
      a: r.cells[gA.key].pct,
      b: r.cells[gB.key].pct,
    }));
    const maxAbs = Math.max(10, ...diffData.map((d) => Math.abs(d.diff)));

    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2">
          {categoryPlural} that differ most between groups
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Bar = difference in {valueNoun} (percentage points). Right favors{' '}
          <Box component="span" sx={{ color: FAVOR_POS, fontWeight: 600 }}>
            {gA.label} (n={gA.denominator.toLocaleString()})
          </Box>; left favors{' '}
          <Box component="span" sx={{ color: FAVOR_NEG, fontWeight: 600 }}>
            {gB.label} (n={gB.denominator.toLocaleString()})
          </Box>.
        </Typography>
        <ResponsiveContainer width="100%" height={Math.max(200, diffData.length * 34 + 40)}>
          <BarChart layout="vertical" data={diffData} margin={{ top: 5, right: 24, left: 8, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              domain={[-maxAbs, maxAbs]}
              tickFormatter={(v) => `${Math.abs(v)}`}
              unit="pp"
              fontSize={12}
            />
            <YAxis type="category" dataKey="category" width={130} fontSize={12} interval={0} />
            <ReferenceLine x={0} stroke="#888" />
            <Tooltip
              formatter={(v, n, p) => [`${p.payload.a}% vs ${p.payload.b}% (Δ ${v}pp)`, p.payload.category]}
              labelFormatter={() => ''}
            />
            <Bar dataKey="diff" isAnimationActive={false}>
              {diffData.map((d) => (
                <Cell key={d.category} fill={d.diff >= 0 ? FAVOR_POS : FAVOR_NEG} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    );
  }
};

// Multi-group prevalence as a heatmap: taxa (rows, by gap) × groups (cols), cell
// shaded by prevalence with the % in the cell. Colour is relative to the shown
// range (prevalences are often low in absolute terms), so it reveals variation;
// the number carries the absolute truth. Built as a CSS grid — recharts has no
// native heatmap.
const PrevalenceHeatmap = ({ comparableGroups, rows, categoryPlural, valueNoun = 'prevalence' }) => {
  const top = rows.slice(0, HEATMAP_ROWS);
  const maxPct = Math.max(
    1,
    ...top.flatMap((r) => comparableGroups.map((g) => r.cells[g.key]?.pct ?? 0))
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {categoryPlural} that differ most between groups ({valueNoun}, % of group)
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `minmax(110px, 1.4fr) repeat(${comparableGroups.length}, minmax(44px, 1fr))`,
          gap: '2px',
          minWidth: 320,
          alignItems: 'stretch',
        }}
      >
        <Box />
        {comparableGroups.map((g) => (
          <Box key={g.key} sx={{ textAlign: 'center', pb: 0.5 }}>
            <Box sx={{ fontSize: 12, fontWeight: 600 }}>{g.label}</Box>
            <Box sx={{ fontSize: 11, color: 'text.secondary' }}>n={g.denominator.toLocaleString()}</Box>
          </Box>
        ))}
        {top.map((r) => (
          <React.Fragment key={r.category}>
            <Box
              title={r.category}
              sx={{
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                pr: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {r.category}
            </Box>
            {comparableGroups.map((g) => {
              const pct = r.cells[g.key]?.pct ?? 0;
              const t = pct / maxPct;
              return (
                <Box
                  key={g.key}
                  sx={{
                    bgcolor: heatColor(t),
                    color: t > 0.55 ? '#fff' : 'text.primary',
                    textAlign: 'center',
                    fontSize: 12,
                    py: 0.75,
                    borderRadius: 0.5,
                  }}
                >
                  {pct}%
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Darker = higher prevalence (relative to the shown range). Rows sorted by between-group gap.
      </Typography>
    </Paper>
  );
};

// Distribution: one 100%-stacked bar per group, segments = classes. Percentages
// are over each group's classified specimens, so bars sum to ~100%.
const DistributionChart = ({ comparableGroups, rows }) => {
  // Stack the segments in natural class order (I, II, III, IV-A…) so the bars and
  // legend read consistently — the table keeps the gap ranking, the chart doesn't.
  const orderedRows = [...rows].sort((a, b) =>
    a.category.localeCompare(b.category, undefined, { numeric: true, sensitivity: 'base' })
  );
  // One datum per group; one numeric key per class (its % in that group). The
  // group's n rides in the axis label so the (possibly uneven) sizes are visible
  // right at the bars.
  const data = comparableGroups.map((g) => {
    const point = { group: `${g.label} (n=${g.denominator.toLocaleString()})` };
    for (const r of orderedRows) point[r.category] = r.cells[g.key]?.pct ?? 0;
    return point;
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Class distribution per group (% of classified specimens)
      </Typography>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="group" fontSize={12} />
          {/* Rounded class percentages can sum just over 100; cap the axis with
              fixed ticks so the top label stays "100%", not "100.199…%". */}
          <YAxis unit="%" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} allowDataOverflow />
          <Tooltip formatter={(v, n) => [`${v}%`, n]} />
          <Legend />
          {orderedRows.map((r, i) => (
            <Bar
              key={r.category}
              dataKey={r.category}
              stackId="dist"
              fill={SERIES_COLORS[i % SERIES_COLORS.length]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default CohortComparison;
